import React from "react";
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  Avatar,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack,
  School,
  AccountCircle,
  ExitToApp,
  VpnKey,
  Home,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme, createTheme, ThemeProvider } from "@mui/material/styles";
import { useAuth } from "../contexts/AuthContext";
import { getAnimalAvatar, getDisplayUsername } from "../utils/userUtils";

// Academic Theme
const academicTheme = createTheme({
  palette: {
    primary: {
      main: "#1565C0",
    },
    secondary: {
      main: "#FF8F00",
    },
    background: {
      default: "#F8F9FA",
      paper: "#FFFFFF",
    },
  },
});

export default function ProfilePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, logout } = useAuth();

  const username =
    user?.username || localStorage.getItem("username") || "ไม่ทราบชื่อผู้ใช้";
  const displayName = getDisplayUsername(username, "student_id");
  const friendlyName = getDisplayUsername(username, "friendly");
  const animalAvatar = getAnimalAvatar(username);

  const handleLogout = () => {
    logout();
    navigate("/academic");
  };

  return (
    <ThemeProvider theme={academicTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #F8F9FA 0%, #E3F2FD 100%)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Navigation Bar */}
        <AppBar position="static" sx={{ backgroundColor: "#1565C0" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate("/dashboard")}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>

            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <School sx={{ fontSize: 32, mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                ข้อมูลส่วนตัว
              </Typography>
            </Box>

            <Button
              startIcon={<Home />}
              sx={{ color: "white" }}
              onClick={() => navigate("/dashboard")}
            >
              หน้าหลัก
            </Button>
          </Toolbar>
        </AppBar>

        {/* Content */}
        <Container
          maxWidth="sm"
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 500,
              width: "100%",
              textAlign: "center",
              borderRadius: 4,
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(21,101,192,0.15)",
              border: "1px solid #E3F2FD",
            }}
          >
            {/* Profile Header */}
            <Box sx={{ mb: 4 }}>
              <Avatar
                sx={{
                  bgcolor: "#1565C0",
                  width: 100,
                  height: 100,
                  mx: "auto",
                  mb: 3,
                  fontSize: 50,
                  fontWeight: "bold",
                  background:
                    "linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)",
                  boxShadow: "0 4px 16px rgba(21,101,192,0.3)",
                }}
              >
                {animalAvatar}
              </Avatar>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#1565C0",
                  mb: 1,
                  fontSize: isMobile ? "1.8rem" : "2.2rem",
                }}
              >
                {displayName}
              </Typography>

              <Typography variant="body1" sx={{ color: "#666", mb: 3 }}>
                {friendlyName} • มหาวิทยาลัยเทคโนโลยีนวัตกรรม
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* User Info Section */}
            <Box sx={{ mb: 4, textAlign: "left" }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1565C0",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <AccountCircle sx={{ mr: 1 }} />
                ข้อมูลผู้ใช้งาน
              </Typography>

              <Box
                sx={{
                  p: 3,
                  bgcolor: "#F8F9FA",
                  borderRadius: 3,
                  border: "1px solid #E3F2FD",
                }}
              >
                <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                  รหัสนักศึกษา
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#1565C0",
                    fontFamily: "monospace",
                    letterSpacing: 1,
                  }}
                >
                  {displayName}
                </Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<VpnKey />}
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)",
                  fontWeight: 600,
                  fontSize: "1rem",
                  boxShadow: "0 4px 16px rgba(21,101,192,0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)",
                    boxShadow: "0 6px 20px rgba(21,101,192,0.4)",
                  },
                }}
                onClick={() => navigate("/change-password")}
              >
                เปลี่ยนรหัสผ่าน
              </Button>

              <Button
                variant="outlined"
                fullWidth
                startIcon={<ExitToApp />}
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderColor: "#D32F2F",
                  color: "#D32F2F",
                  "&:hover": {
                    backgroundColor: "#D32F2F",
                    color: "white",
                    borderColor: "#D32F2F",
                  },
                }}
                onClick={handleLogout}
              >
                ออกจากระบบ
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
